import { ENV } from '../../config/runtimeEnv';
import { Patient } from "../../types/healthcare/PatientTypes";
import type { LegacyUserId } from "../../types/identity";
import { sessionLabiqUserId, toLabiqPatient } from "../../utils/auth/labiqUserId";
import { http } from "../../lib/http/client";

const API_BASE_URL = ENV.VITE_LABIQ_SERVER;

// Types for the API responses
export interface MethylationAnalysis {
	genetic_variants: Record<string, string>;
	classifications: {
		methylator_type: string;
		capacity_level: string;
		demand_level: string;
		comt_type: string;
		homocysteine_level: string;
		folate_level: string;
		b12_level: string;
	};
	methylation_results: {
		overall_score: number;
		risk_level: string;
		capacity_score: number;
		demand_score: number;
	};
	nutrient_pressure_points: Array<{
		name: string;
		level: string;
	}>;
	explanations: Record<string, string>;
}

export interface SampleVariants {
	available_variants: Array<{
		gene: string;
		rsid: string;
	}>;
	missing_variants: Array<{
		gene: string;
		rsid: string;
	}>;
	methylation_ready: boolean;
	completeness_percentage: number;
}

export interface ReportGenerationResponse {
	message: string;
	file_id: string;
	file_name: string;
}

export interface AudiobookResponse {
	message: string;
	status: string;
	job_id?: string;
	estimated_completion_time?: string;
	note?: string;
}

export interface AudiobookChapter {
	chapter: string;
	content: string;
}

export interface ChapterStatistic {
	number: number;
	title: string;
	character_count: number;
	word_count: number;
	estimated_minutes: number;
}

export interface AudiobookChaptersResponse {
	success: boolean;
	sample_id: string;
	patient_id: LegacyUserId;
	generated_at: string;
	chapters: AudiobookChapter[];
	statistics: {
		total_chapters: number;
		chapters: ChapterStatistic[];
		total_length: number;
		average_length: number;
	};
}

export interface SingleChapterResponse {
	success: boolean;
	sample_id: string;
	chapter_number: number;
	chapter: AudiobookChapter;
}

// Generate Methylation Report
export const generateMethylationReport = async (
	patient: Patient,
	sampleId: string,
): Promise<ReportGenerationResponse> => {
	const response = await http.post(
		`${API_BASE_URL}/reports/methylation`,
		{
			sample_id: sampleId,
			patient: JSON.stringify(toLabiqPatient(patient)),
		}
	);

	return response.data;
};

// Generate Genetic Report
export const generateGeneticReport = async (
	patient: Patient,
	sampleId: string,
): Promise<ReportGenerationResponse> => {
	const response = await http.post(
		`${API_BASE_URL}/reports/genetic`,
		{
			sample_id: sampleId,
			patient: JSON.stringify(toLabiqPatient(patient)),
			user_id: sessionLabiqUserId()
		}
	);

	return response.data;
};

export const generateActionReport = async (
	patient: Patient,
	sampleId: string,
): Promise<ReportGenerationResponse> => {
	const response = await http.post(
		`${API_BASE_URL}/reports/action-plan`,
		{
			sample_id: sampleId,
			patient: JSON.stringify(toLabiqPatient(patient)),
		}
	);

	return response.data;
};


// Get Methylation Analysis Preview
export const getMethylationAnalysis = async (
	sampleId: string,
): Promise<MethylationAnalysis> => {
	const response = await http.get(
		`${API_BASE_URL}/reports/analysis/methylation/${sampleId}`
	);

	return response.data.data;
};

// Get Sample Variants Information
export const getSampleVariants = async (
	sampleId: string,
): Promise<SampleVariants> => {
	const response = await http.get(
		`${API_BASE_URL}/reports/samples/${sampleId}/variants`
	);

	return response.data;
};

// Generate Audiobook Report
//
// `sourceType` (M16, ClickUp 86d3r82eh) says WHICH REPORT the audiobook is made from —
// `"labiq_methylation_report"` or `"labiq_genetic_report"`. It is a registry key that
// xoe-agent resolves to a chapter plan; **the frontend never sees chapter prose**, and an
// unrecognised key is refused by the agent rather than silently narrating the wrong plan.
//
// It is REQUIRED here even though labiq's validation makes it optional: the only caller is
// `ReportTab`'s resolver, which already knows the answer from the file name, and a
// defaulted argument would let a future call site quietly generate a methylation audiobook
// from a genetic PDF. The optionality exists server-side for OLD clients, not for new ones.
export const createAudiobookReport = async (
	patientId: LegacyUserId,
	fileId: string,
	sampleId: string,
	sourceType: string,
): Promise<AudiobookResponse> => {
	const response = await http.post(
		`${API_BASE_URL}/reports/audiobook`,
		{
			file_id: fileId,
			patient_id: patientId,
			sample_id: sampleId,
			source_type: sourceType,
			user_id: sessionLabiqUserId()
		},
		{
			timeout: 600000,
		}
	);

	return response.data;
};

// Get Audiobook Chapters for a Sample
export const getAudiobookChapters = async (
	sampleId: string,
): Promise<AudiobookChaptersResponse> => {
	const response = await http.get(
		`${API_BASE_URL}/reports/audiobook/chapters/${sampleId}`
	);

	return response.data;
};

// Get Specific Audiobook Chapter
export const getAudiobookChapter = async (
	sampleId: string,
	chapterNumber: number,
): Promise<SingleChapterResponse> => {
	const response = await http.get(
		`${API_BASE_URL}/reports/audiobook/chapters/${sampleId}/${chapterNumber}`
	);

	return response.data;
};

// Get Chapter Statistics
export const getAudiobookStatistics = async (
	sampleId: string,
): Promise<{ success: boolean; statistics: any }> => {
	const response = await http.get(
		`${API_BASE_URL}/reports/audiobook/chapters/${sampleId}/statistics`
	);

	return response.data;
};

// Regenerate Audiobook from Stored Chapters
export const regenerateAudiobookFromChapters = async (
	sampleId: string,
	patientId: LegacyUserId,
): Promise<AudiobookResponse> => {
	const response = await http.post(
		`${API_BASE_URL}/reports/audiobook/regenerate-from-chapters`,
		{
			sample_id: sampleId,
			patient_id: patientId,
			user_id: sessionLabiqUserId()
		}
	);

	return response.data;
};
